import React, { useCallback } from 'react';
import { cn } from '../../design-system/cn';
import { consoleMotion, touchTarget } from '../../design-system/tokens';
import {
  MY_ARENA_TAB_COMPLETED,
  MY_ARENA_TAB_RECORD,
} from '../../constants/productCopy';

/**
 * Lifecycle tabs — PWA concept labels (Locks / Tickets / Challenges).
 * Engine tab ids unchanged: active / sent / open / completed / history.
 */
export const ARENA_LIFECYCLE_TABS = [
  { id: 'active', label: 'Locks' },
  { id: 'sent', label: 'Tickets' },
  { id: 'open', label: 'Challenges' },
  { id: 'completed', label: MY_ARENA_TAB_COMPLETED },
  { id: 'history', label: MY_ARENA_TAB_RECORD },
];

export function ArenaSegmentedTabs({
  tabs,
  activeId,
  onChange,
  ariaLabel,
  className,
  'data-testid': testId,
}) {
  const focusTab = useCallback((index) => {
    const el = document.getElementById(`arena-seg-tab-${tabs[index]?.id}`);
    el?.focus();
  }, [tabs]);

  const onKeyDown = useCallback((event) => {
    const currentIndex = tabs.findIndex((tab) => tab.id === activeId);
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;
    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    onChange?.(tabs[nextIndex].id);
    focusTab(nextIndex);
  }, [activeId, focusTab, onChange, tabs]);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn('flex gap-5 border-b border-white/[0.08] overflow-x-auto scrollbar-hide', className)}
      onKeyDown={onKeyDown}
    >
      {tabs.map((tab) => {
        const selected = activeId === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`arena-seg-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={tab.panelId}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange?.(tab.id)}
            className={cn(
              'shrink-0 pb-2.5 text-[11px] font-black uppercase tracking-widest transition-colors',
              touchTarget.comfortable,
              consoleMotion.press,
              consoleMotion.focus,
              selected
                ? 'text-[#8cff4a] border-b-2 border-[#8cff4a] -mb-px'
                : 'text-[#687386] hover:text-[#9da7b6] border-b-2 border-transparent',
            )}
          >
            {tab.label}
            {tab.count != null && tab.count > 0 ? (
              <span className="ml-1 tabular-nums opacity-80">({tab.count})</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default function ArenaLifecycleTabs({
  activeTab,
  onTabChange,
  counts = {},
  className,
}) {
  const tabs = ARENA_LIFECYCLE_TABS.map((tab) => ({
    ...tab,
    count: counts[tab.id] ?? 0,
    panelId: `arena-panel-${tab.id}`,
  }));

  return (
    <ArenaSegmentedTabs
      tabs={tabs}
      activeId={activeTab}
      onChange={onTabChange}
      ariaLabel="My Arena lifecycle"
      data-testid="arena-lifecycle-tabs"
      className={className}
    />
  );
}
